import type { PrimaryTab } from "./primaryNav";

/**
 * The redesign's no-feature-loss contract for web navigation.
 *
 * Every first route segment mounted in App.tsx must appear here, even when the
 * route is a legacy redirect, a legal surface, or a temporarily unavailable
 * compatibility path. This keeps shell redesigns from silently assigning a
 * mature feature to Today merely because it was omitted from a local map.
 */
export interface FeatureRouteOwner {
  pathPrefix: string;
  feature: string;
  owner: PrimaryTab;
}

export const FEATURE_ROUTE_OWNERS: readonly FeatureRouteOwner[] = [
  { pathPrefix: "", feature: "Living Today", owner: "today" },
  { pathPrefix: "android", feature: "Android app introduction", owner: "today" },
  { pathPrefix: "today", feature: "Personal and world day", owner: "today" },
  { pathPrefix: "landing", feature: "Product introduction", owner: "today" },
  { pathPrefix: "clock", feature: "Spacetime warp clock", owner: "today" },
  { pathPrefix: "calendar", feature: "Calendar and horas", owner: "today" },
  { pathPrefix: "alerts", feature: "Daily alerts", owner: "today" },
  { pathPrefix: "account", feature: "Account settings", owner: "today" },
  { pathPrefix: "plans", feature: "Plans and entitlements", owner: "today" },
  { pathPrefix: "privacy", feature: "Privacy", owner: "today" },
  { pathPrefix: "terms", feature: "Terms", owner: "today" },
  { pathPrefix: "contact", feature: "Contact", owner: "today" },
  { pathPrefix: "how-it-works", feature: "How it works", owner: "today" },
  { pathPrefix: "sample-reading", feature: "Sample reading", owner: "today" },
  { pathPrefix: "faq", feature: "FAQ", owner: "today" },

  { pathPrefix: "cast", feature: "Winding", owner: "watch" },
  { pathPrefix: "watch", feature: "Watch", owner: "watch" },
  { pathPrefix: "reading", feature: "Face", owner: "watch" },
  { pathPrefix: "houses", feature: "Twelve houses", owner: "watch" },
  { pathPrefix: "planetary-status", feature: "Planetary status", owner: "watch" },
  { pathPrefix: "compute", feature: "Chart computation", owner: "watch" },
  { pathPrefix: "ephemeris", feature: "Ephemeris", owner: "watch" },
  { pathPrefix: "panchang", feature: "Panchang", owner: "watch" },
  { pathPrefix: "journey", feature: "Journey timeline", owner: "watch" },
  { pathPrefix: "movement", feature: "Movement and Ages", owner: "watch" },
  { pathPrefix: "predictions", feature: "Life predictions", owner: "watch" },
  { pathPrefix: "prediction", feature: "Prediction detail", owner: "watch" },
  { pathPrefix: "dashas", feature: "Dasha systems", owner: "watch" },
  { pathPrefix: "council", feature: "Council", owner: "watch" },
  { pathPrefix: "three-council", feature: "Three-tradition council", owner: "watch" },
  { pathPrefix: "sky", feature: "Personal sky", owner: "watch" },
  { pathPrefix: "rectify", feature: "Birth-time rectification", owner: "watch" },

  { pathPrefix: "ask", feature: "Horary Ask", owner: "ask" },
  { pathPrefix: "horary", feature: "Horary compatibility route", owner: "ask" },
  { pathPrefix: "best-dates", feature: "Best dates", owner: "ask" },
  { pathPrefix: "event-timing", feature: "Event timing", owner: "ask" },

  { pathPrefix: "listen", feature: "Listen hub", owner: "listen" },
  { pathPrefix: "radio", feature: "Transit Radio", owner: "listen" },
  { pathPrefix: "transit-radio", feature: "Transit Radio compatibility route", owner: "listen" },
  { pathPrefix: "life-music", feature: "Life Music", owner: "listen" },
  { pathPrefix: "station", feature: "The station", owner: "listen" },
  { pathPrefix: "sound", feature: "Raga methodology", owner: "listen" },

  { pathPrefix: "discover", feature: "Discover hub", owner: "discover" },
  { pathPrefix: "space-lab", feature: "Space Lab", owner: "discover" },
  { pathPrefix: "planner", feature: "Electional Planner", owner: "discover" },
  { pathPrefix: "mundane", feature: "Mundane and Mundane Studio", owner: "discover" },
  { pathPrefix: "mundane-studio", feature: "Mundane Studio", owner: "discover" },
  { pathPrefix: "dasha-analogs", feature: "Period analogs", owner: "discover" },
  { pathPrefix: "benchmark", feature: "Validation benchmark", owner: "discover" },
  { pathPrefix: "archive", feature: "Assessment archive", owner: "discover" },
  { pathPrefix: "saved", feature: "Saved watches", owner: "discover" },
  { pathPrefix: "cases", feature: "Case library", owner: "discover" },
  { pathPrefix: "celebrity", feature: "Celebrity assessment", owner: "discover" },
  { pathPrefix: "celebrities", feature: "Celebrity watches", owner: "discover" },
  { pathPrefix: "library", feature: "Classical library and discoveries", owner: "discover" },
  { pathPrefix: "rules", feature: "Rules library", owner: "discover" },
  { pathPrefix: "tools", feature: "Advanced tools", owner: "discover" },
  { pathPrefix: "features", feature: "Feature modules", owner: "discover" },
  { pathPrefix: "export", feature: "Export", owner: "discover" },
  { pathPrefix: "import", feature: "Import", owner: "discover" },
  { pathPrefix: "search", feature: "Universal search", owner: "discover" },
  { pathPrefix: "synastry", feature: "Synastry compatibility route", owner: "discover" },
  { pathPrefix: "relationship", feature: "Relationship compatibility route", owner: "discover" },
] as const;

const OWNER_BY_PREFIX = new Map(
  FEATURE_ROUTE_OWNERS.map(({ pathPrefix, owner }) => [pathPrefix, owner]),
);

export function featureOwnerForPath(pathname: string): PrimaryTab {
  const prefix = pathname.split("/").filter(Boolean)[0] ?? "";
  return OWNER_BY_PREFIX.get(prefix) ?? "today";
}

export function hasRegisteredFeaturePrefix(prefix: string): boolean {
  return OWNER_BY_PREFIX.has(prefix);
}
